import React, { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { CircularProgress } from '@mui/material'
import DetailShell, { useDetailTab } from 'components/DetailShell'
import { useAccessCount } from 'features/accessControl/hooks/useAccessCount'
import PassportHeader, { PassportAction, PassportDangerAction, PassportOverviewIntro } from 'components/PassportHeader'
import FeatureSwitchBar from 'components/FeatureSwitchBar'
import { hideEmail } from 'utils/user'
import { getPortfolioRoutine, removeBpFromPortfolioRoutine, addBpsToPortfolioRoutine, deletePortfolioRoutine, updatePortfolioRoutine } from 'ducks/portfolio/actions'
import { selectSelectedPortfolio, selectPortfolioLoading, selectPortfolioError } from 'ducks/portfolio/selectors'
import { selectComplianceEnabled, selectFeatureVisibility, selectFeatureDefaultOwner } from 'features/auth/ducks/selectors'
import { isFeatureVisible } from 'constants/featureGroups'
import { PRIVATE_PATHS } from 'utils/paths'
import BpPicker from './components/BpPicker'
import OverviewTab from './components/OverviewTab'
import PortfolioDocumentsSection from './components/PortfolioDocumentsSection'
import AuditTab from './components/AuditTab'
import AccessManager from 'features/accessControl/AccessManager'
import GuruChat from 'features/bpDetails/Components/guru/GuruChat'
import RemedialsWorkspace from 'features/actions/workspace/RemedialsWorkspace'
import IncidentsPage from 'features/incidents/IncidentsPage'
import PortfolioTasksTab from 'features/portfolio/components/PortfolioTasksTab'
import PortfolioBuildingsEstates from 'features/portfolio/PortfolioBuildingsEstates'
import ActivityLog from 'components/ActivityLog'
import VisibilityIcon from '@mui/icons-material/Visibility'
import EditNoteIcon from '@mui/icons-material/EditNote'
import ScheduleIcon from '@mui/icons-material/Schedule'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import LockPersonOutlinedIcon from '@mui/icons-material/LockPersonOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import { updatePortfolio } from 'services/portfolioService'
import { listPortfolioEstates } from 'services/estateService'
import CreatePortfolioModal from './components/CreatePortfolioModal'
import PortfolioOwnerDialog from './components/PortfolioOwnerDialog'
import CompliancePulse from 'features/bpDetails/Components/CompliancePulse'
import PassportsMap from 'features/portfolios/components/PassportsMap'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined'
import { toast } from 'react-toastify'

const PortfolioDetail = () => {
  const { id } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const portfolio = useSelector(selectSelectedPortfolio)
  const isLoading = useSelector(selectPortfolioLoading)
  const loadError = useSelector(selectPortfolioError)
  const complianceEnabled = useSelector(selectComplianceEnabled)
  const featureVisibility = useSelector(selectFeatureVisibility)
  const featureOwner = useSelector(selectFeatureDefaultOwner)
  const tasksVisible = isFeatureVisible(featureVisibility, 'tasks', featureOwner)
  const incidentsVisible = isFeatureVisible(featureVisibility, 'incidents', featureOwner)
  const canUseGuru = !!portfolio && (!!portfolio.isOwner || (portfolio.buildingPassports || []).length > 0)
  const [activeTab, setActiveTab] = useDetailTab(canUseGuru ? 'guru' : 'buildings')
  const currentUserId = useSelector(state => state.auth?.user?.id ?? null)
  const [accessCount, updateAccessCount] = useAccessCount('portfolio', id, !!portfolio && (!!portfolio.isOwner || portfolio.currentUserPermission === 'editor'), currentUserId)
  const [showBpPicker, setShowBpPicker] = useState(false)
  const [overviewKey, setOverviewKey] = useState(0)
  const [scheduleBusy, setScheduleBusy] = useState(null)
  const [recipientsInput, setRecipientsInput] = useState('')
  const [savingRecipients, setSavingRecipients] = useState(false)

  useEffect(() => {
    if (id) dispatch(getPortfolioRoutine(id))
  }, [id, dispatch])

  const savedRecipients = (portfolio?.reportRecipients || []).join(', ')
  useEffect(() => { setRecipientsInput(savedRecipients) }, [savedRecipients])

  const [estates, setEstates] = useState(null)
  const tabContentRef = useRef(null)
  const [editing, setEditing] = useState(false)
  const [editingOwner, setEditingOwner] = useState(false)

  useEffect(() => {
    if (!id) return undefined
    let active = true
    setEstates(null)
    listPortfolioEstates(id)
      .then(res => { if (active) setEstates(res?.data?.data || []) })
      .catch(() => { if (active) setEstates([]) })
    return () => { active = false }
  }, [id])


  const handleRemoveBp = (bpId) => {
    if (window.confirm('Remove this Building Passport from the portfolio?')) {
      dispatch(removeBpFromPortfolioRoutine({ portfolioId: id, bpId }))
      setOverviewKey(k => k + 1) // Invalidate overview cache
    }
  }

  const handleAddBps = (bpIds) => {
    dispatch(addBpsToPortfolioRoutine({ portfolioId: id, bpIds }))
    setShowBpPicker(false)
    setOverviewKey(k => k + 1) // Invalidate overview cache
  }

  const handleEditPortfolio = values => {
    dispatch(updatePortfolioRoutine({ id, ...values }))
    setEditing(false)
  }

  const handleDeletePortfolio = () => {
    if (window.confirm('Delete this portfolio? The Building Passports in it are not deleted, they just leave the group.')) {
      dispatch(deletePortfolioRoutine(id))
      navigate(PRIVATE_PATHS.portfolios)
    }
  }

  const isCurrent = portfolio?.id === id
  if (!isCurrent || isLoading) {
    return (
      <DetailShell
        backLabel='Back to Portfolios'
        onBack={() => navigate(PRIVATE_PATHS.portfolios)}
        loading={!loadError || isLoading}
        notFound={Boolean(loadError) && !isLoading}
      />
    )
  }

  const bps = portfolio.buildingPassports || []
  const allBuildingCount = new Set([
    ...bps.map(b => b.id),
    ...(estates || []).flatMap(e => (e.buildingPassports || []).map(b => b.id)),
  ]).size
  const totalBpCount = portfolio.totalBpCount ?? bps.length
  const isOwner = !!portfolio.isOwner
  const canEdit = isOwner || portfolio.currentUserPermission === 'editor'
  const hiddenBpCount = Math.max(0, totalBpCount - bps.length)

  // Access tiers for portfolio-wide features:
  //  - Overview & Guru Chat compute live per-viewer from accessible BPs, so they
  //    work with any access (≥1 BP).
  //  - Audit is a stored snapshot covering every BP at the time it ran and can't
  //    be refiltered by viewer — it requires full access to every BP.
  const hasAnyBpAccess = isOwner || bps.length > 0
  const hasFullBpAccess = isOwner || (totalBpCount > 0 && bps.length === totalBpCount)
  // Actions are only useful where Task Management is enabled. Tab visible if any visible BP has it.
  const anyBpHasTaskManagement = bps.some(bp => bp.hasTaskManagement)
  const ownerName = portfolio.owner?.name || 'the owner'
  const partialHint = hasAnyBpAccess
    ? null
    : `Requires access to at least one Building Passport. Ask ${ownerName} to share the Building Passports in this portfolio.`
  const auditHint = hasFullBpAccess
    ? null
    : bps.length === 0
      ? `Requires access to all ${totalBpCount} Building Passports. Ask ${ownerName} to share them with you.`
      : `Audit requires access to every Building Passport. You have ${bps.length} of ${totalBpCount}. Ask ${ownerName} to share the remaining ${hiddenBpCount}.`
  const tmHint = !anyBpHasTaskManagement
    ? 'Requires Task Management on at least one Building Passport in this portfolio.'
    : null
  // A hint that explains why something is locked always wins over the plain
  // description of what the section is.
  const sectionHint = (locked, reason, description) => (locked ? reason : description)

  const tabs = [
    { key: 'buildings', label: 'Buildings & estates', icon: ApartmentOutlinedIcon, dataTourId: 'passport.buildings-tab', count: allBuildingCount, hint: 'Every building and estate in this portfolio.' },
    { key: 'overview', label: 'Overview', icon: DashboardOutlinedIcon, disabled: !hasAnyBpAccess, hint: sectionHint(!hasAnyBpAccess, partialHint, 'Compliance and activity across the whole portfolio at a glance.') },
    { key: 'documents', label: 'Documents', icon: FolderOutlinedIcon, hint: 'Documents held for this portfolio, plus anything shared up from its buildings.' },
    ...(complianceEnabled && anyBpHasTaskManagement ? [{ key: 'audit', label: 'Compliance Audit', icon: FactCheckOutlinedIcon, disabled: !hasFullBpAccess, hint: sectionHint(!hasFullBpAccess, auditHint, 'Check the evidence behind each building\u2019s compliance score.') }] : []),
    { key: 'guru', label: 'Guru Chat', icon: AutoAwesomeOutlinedIcon, disabled: !hasAnyBpAccess, hint: sectionHint(!hasAnyBpAccess, partialHint, 'Ask questions about any document across this portfolio.') },
    {
      key: 'tasks',
      label: 'Tasks',
      icon: AssignmentOutlinedIcon,
      disabled: !hasAnyBpAccess || !anyBpHasTaskManagement,
      hint: sectionHint(!hasAnyBpAccess || !anyBpHasTaskManagement, !hasAnyBpAccess ? partialHint : tmHint, 'Scheduled work across every building in this portfolio.'),
    },
    {
      key: 'actions',
      label: 'Remedials',
      icon: ChecklistOutlinedIcon,
      disabled: !hasAnyBpAccess || !anyBpHasTaskManagement,
      hint: sectionHint(!hasAnyBpAccess || !anyBpHasTaskManagement, !hasAnyBpAccess ? partialHint : tmHint, 'Problems found that still need putting right, across the portfolio.'),
    },
    {
      key: 'incidents',
      label: 'Incidents',
      icon: ReportProblemOutlinedIcon,
      disabled: !hasAnyBpAccess || !anyBpHasTaskManagement,
      hint: sectionHint(!hasAnyBpAccess || !anyBpHasTaskManagement, !hasAnyBpAccess ? partialHint : tmHint, 'Reported incidents and near misses across the portfolio.'),
    },
    ...(canEdit ? [{ key: 'access', label: 'Access', icon: LockPersonOutlinedIcon, dataTourId: 'passport.access-tab', count: accessCount ? accessCount.toLocaleString('en-GB') : undefined, hint: accessCount ? `${accessCount.toLocaleString('en-GB')} ${accessCount === 1 ? 'person has' : 'people have'} access to this portfolio. See who they are and what they are allowed to do.` : 'Who can see this portfolio, and what they are allowed to do.' }] : []),
    { key: 'activity', label: 'Activity', icon: HistoryOutlinedIcon, hint: 'A plain-language record of what has happened in this portfolio.' },
  ].filter(t => {
    if (t.key === 'tasks' || t.key === 'actions') return tasksVisible
    if (t.key === 'incidents') return incidentsVisible
    return true
  })

  const currentSchedule = portfolio.auditSchedule || 'off'

  const handleScheduleChange = async (value) => {
    if (value === currentSchedule || scheduleBusy) return
    setScheduleBusy(value)
    try {
      const res = await updatePortfolio(id, { audit_schedule: value })
      if (res.data?.data?.scheduleActivated) {
        toast.success(`Automated audits set to ${value}. Running the first audit now. You will get an email when it is ready.`)
        setActiveTab('audit')
      } else if (value === 'off') {
        toast.success('Automated audits turned off')
      } else {
        const every = value === 'weekly' ? '7 days' : '30 days'
        toast.success(`Automated audits set to ${value}. The next one runs within ${every}. We have not started a new audit now, because your latest report is still up to date.`)
      }
      dispatch(getPortfolioRoutine(id))
    } catch (e) {
      toast.error('Failed to update audit schedule')
    } finally {
      setScheduleBusy(null)
    }
  }

  const saveRecipients = async () => {
    if (savingRecipients) return
    const emails = recipientsInput.split(',').map(e => e.trim()).filter(Boolean)
    const invalid = emails.find(e => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e))
    if (invalid) {
      toast.error(`"${invalid}" is not a valid email address.`)
      return
    }
    const unique = [...new Set(emails.map(e => e.toLowerCase()))]
    setSavingRecipients(true)
    try {
      await updatePortfolio(id, { report_recipients: unique })
      toast.success(unique.length
        ? `Saved. The report will also be sent to ${unique.length} recipient${unique.length === 1 ? '' : 's'}.`
        : 'Saved. Extra recipients cleared.')
      dispatch(getPortfolioRoutine(id))
    } catch (e) {
      toast.error('Could not save the recipients. Please try again.')
    } finally {
      setSavingRecipients(false)
    }
  }

  const nextAuditDate = (() => {
    if (currentSchedule === 'off' || !portfolio.lastAutoAuditAt) return null
    const days = currentSchedule === 'weekly' ? 7 : 30
    const last = new Date(portfolio.lastAutoAuditAt)
    const next = new Date(last.getTime() + days * 24 * 60 * 60 * 1000)
    return next.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
  })()

  const auditTabAvailable = complianceEnabled && anyBpHasTaskManagement && hasFullBpAccess
  const createdOn = portfolio.createdAt
    ? new Date(portfolio.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null

  const overviewIntro = (
    <PassportOverviewIntro
      highlight={hasAnyBpAccess && anyBpHasTaskManagement ? (
        <CompliancePulse
          compact
          portfolioId={id}
          showCompliance={complianceEnabled}
          showTasks={tasksVisible}
          showIncidents={incidentsVisible}
        />
      ) : null}
      fields={[
        {
          label: 'Owner',
          icon: PersonOutlineOutlinedIcon,
          value: portfolio.ownerOrganisation || 'Not set yet',
          onClick: canEdit ? () => setEditingOwner(true) : undefined,
          title: canEdit
            ? 'The organisation that owns these buildings. Click to change it.'
            : 'The organisation that owns these buildings.',
        },
        createdOn ? { label: 'Created', icon: EventOutlinedIcon, value: createdOn } : null,
        auditTabAvailable ? {
          label: 'Automated audits',
          icon: EventRepeatIcon,
          value: currentSchedule === 'off' ? 'Manual' : currentSchedule.charAt(0).toUpperCase() + currentSchedule.slice(1),
          detail: currentSchedule === 'off' ? 'Run by hand' : nextAuditDate ? `Next ${nextAuditDate}` : null,
          onClick: () => setActiveTab('audit'),
          title: 'How often the compliance audit runs. Opens the audit settings.'
        } : null,
        { label: 'Description', icon: NotesOutlinedIcon, value: portfolio.description, wrap: true },
      ]}
    />
  )

  return (
    <DetailShell
      backLabel='Back to Portfolios'
      onBack={() => navigate(PRIVATE_PATHS.portfolios)}
      headerCard={(
        <PassportHeader
          kind='portfolio'
          name={portfolio.name}
          toolbar={<FeatureSwitchBar surface='passport' isOwner={featureOwner} compact />}
          actions={(
            <>
              {hasAnyBpAccess && (
                <PassportAction
                  as='button'
                  type='button'
                  onClick={() => navigate(`${PRIVATE_PATHS.dashboard}?altitude=portfolio&entityId=${id}`)}
                  title='See this portfolio on the compliance dashboard'
                >
                  <SpaceDashboardOutlinedIcon style={{ fontSize: 16 }} /> Dashboard
                </PassportAction>
              )}
              {canEdit && (
                <PassportAction as='button' type='button' data-tour-id='portfolioDetail.add-bps' onClick={() => { setActiveTab('buildings'); setShowBpPicker(true) }} title='Add Building Passports to this portfolio'>
                  <AddRoundedIcon style={{ fontSize: 16 }} /> Add Building Passports
                </PassportAction>
              )}
              {isOwner && (
                <PassportAction as='button' type='button' onClick={() => setEditing(true)} title='Rename this portfolio or change its description'>
                  <EditOutlinedIcon style={{ fontSize: 16 }} /> Edit
                </PassportAction>
              )}
              {isOwner && (
                <PassportDangerAction as='button' type='button' onClick={handleDeletePortfolio} title='Delete this portfolio'>
                  <DeleteOutlineIcon style={{ fontSize: 16 }} /> Delete
                </PassportDangerAction>
              )}
            </>
          )}
          complianceScope={complianceEnabled && anyBpHasTaskManagement && hasAnyBpAccess ? { portfolioId: id } : null}
          onOpenCompliance={() => setActiveTab('overview')}
        />
      )}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      headerExtra={(
        <>
      {!isOwner && (
        <SharePanel>
          <SharePanelAccent />
          <SharePanelBody>
            <SharePanelTitle>
              Shared with you{portfolio.owner?.name ? ` by ${portfolio.owner.name}` : ''}
              {portfolio.sharedByDomain ? ` via @${(portfolio.owner?.email || '').split('@')[1] || 'your domain'}` : ''}
            </SharePanelTitle>
            <SharePanelMeta>
              {portfolio.currentUserPermission === 'editor' ? (
                <><EditNoteIcon style={{ fontSize: 14 }} /> Editor access</>
              ) : (
                <><VisibilityIcon style={{ fontSize: 14 }} /> View-only access</>
              )}
              {hiddenBpCount > 0 && (
                <>
                  <MetaDot>·</MetaDot>
                  <MetaAccent>
                    {bps.length === 0
                      ? `No Building Passport access (${hiddenBpCount} hidden)`
                      : `${bps.length} of ${totalBpCount} Building Passports accessible`}
                  </MetaAccent>
                </>
              )}
            </SharePanelMeta>
            {hiddenBpCount > 0 && (
              <SharePanelNote>
                {bps.length === 0
                  ? `Every portfolio feature is unavailable until ${ownerName} shares at least one Building Passport with you.`
                  : `Overview and Guru Chat work on the ${bps.length} Building Passport${bps.length === 1 ? '' : 's'} you can see. Results will not include the ${hiddenBpCount} you cannot see. The Audit is locked until ${ownerName} shares all ${totalBpCount} Building Passports (it's a snapshot covering every Building Passport).`}
                {!canEdit && ` Because you only have access to some of its Building Passports, you also cannot see this portfolio's Access controls. To see and manage who has access, a portfolio manager or the owner needs to add you to the whole portfolio.`}
              </SharePanelNote>
            )}
          </SharePanelBody>
        </SharePanel>
      )}


        </>
      )}
    >

      <div ref={tabContentRef} style={{ scrollMarginTop: 88 }} />
      {activeTab === 'buildings' && (
        <TabContent>
          {!hasAnyBpAccess && overviewIntro}
          {bps.length > 0 && (
            <PassportsMap
              points={bps.map(b => ({ id: b.id, name: b.name, postcode: b.postalCode }))}
              onOpenBp={bpId => navigate(`/bp-list/${bpId}`)}
              showCompliance={false}
              fitToPoints
            />
          )}
          <PortfolioBuildingsEstates
            portfolioId={id}
            buildingPassports={bps}
            canEdit={canEdit}
            showCompliance={complianceEnabled && anyBpHasTaskManagement}
            onAddBps={() => setShowBpPicker(true)}
            onRemoveBp={handleRemoveBp}
          />

          <BpPicker
            open={showBpPicker}
            onClose={() => setShowBpPicker(false)}
            onSubmit={handleAddBps}
            existingBpIds={bps.map(bp => bp.id)}
          />
        </TabContent>
      )}

      {activeTab === 'overview' && hasAnyBpAccess && (
        <TabContent>
          {overviewIntro}
          <OverviewTab portfolioId={id} key={overviewKey} />
        </TabContent>
      )}
      {activeTab === 'documents' && (
        <PortfolioDocumentsSection
          portfolioId={id}
          canEdit={isOwner || portfolio.currentUserPermission === 'editor'}
          buildingPassports={bps}
        />
      )}
      {activeTab === 'audit' && hasFullBpAccess && complianceEnabled && anyBpHasTaskManagement && (
        <>
          <SchedulePanel>
            <ScheduleHead>
              <ScheduleTitle>
                <ScheduleIcon style={{ fontSize: 18, color: '#D24724' }} />
                Automated audits
              </ScheduleTitle>
              <ScheduleSub>
                Run an Information Risk Assessment on a schedule and email the report
                {portfolio.owner?.email
                  ? <> to you (<strong>{hideEmail(portfolio.owner.email)}</strong>).</>
                  : <> to you.</>}
                {currentSchedule !== 'off' && nextAuditDate && (
                  <> Next automated run: <strong>{nextAuditDate}</strong>.</>
                )}
              </ScheduleSub>
            </ScheduleHead>
            <ScheduleOptions>
              <ScheduleOption
                $active={currentSchedule === 'off'}
                $busy={scheduleBusy === 'off'}
                disabled={!!scheduleBusy}
                onClick={() => handleScheduleChange('off')}
              >
                {scheduleBusy === 'off'
                  ? <CircularProgress size={16} sx={{ color: '#D24724' }} />
                  : <EventBusyIcon style={{ fontSize: 18 }} />}
                <span>
                  <strong>{scheduleBusy === 'off' ? 'Turning off…' : 'Off'}</strong>
                  <em>Manual only</em>
                </span>
              </ScheduleOption>
              <ScheduleOption
                $active={currentSchedule === 'weekly'}
                $busy={scheduleBusy === 'weekly'}
                disabled={!!scheduleBusy}
                onClick={() => handleScheduleChange('weekly')}
              >
                {scheduleBusy === 'weekly'
                  ? <CircularProgress size={16} sx={{ color: '#D24724' }} />
                  : <EventRepeatIcon style={{ fontSize: 18 }} />}
                <span>
                  <strong>{scheduleBusy === 'weekly' ? 'Starting weekly…' : 'Weekly'}</strong>
                  <em>{scheduleBusy === 'weekly' ? 'Running first audit' : 'Every 7 days'}</em>
                </span>
              </ScheduleOption>
              <ScheduleOption
                $active={currentSchedule === 'monthly'}
                $busy={scheduleBusy === 'monthly'}
                disabled={!!scheduleBusy}
                onClick={() => handleScheduleChange('monthly')}
              >
                {scheduleBusy === 'monthly'
                  ? <CircularProgress size={16} sx={{ color: '#D24724' }} />
                  : <EventRepeatIcon style={{ fontSize: 18 }} />}
                <span>
                  <strong>{scheduleBusy === 'monthly' ? 'Starting monthly…' : 'Monthly'}</strong>
                  <em>{scheduleBusy === 'monthly' ? 'Running first audit' : 'Every 30 days'}</em>
                </span>
              </ScheduleOption>
            </ScheduleOptions>
            {currentSchedule !== 'off' && (
              <RecipientsRow>
                <RecipientsLabel>Also email the report to (optional)</RecipientsLabel>
                <RecipientsControls>
                  <RecipientsInput
                    value={recipientsInput}
                    onChange={e => setRecipientsInput(e.target.value)}
                    placeholder='name@example.com, another@example.com'
                    disabled={savingRecipients}
                  />
                  <SaveRecipientsBtn type='button' disabled={savingRecipients} onClick={saveRecipients}>
                    {savingRecipients ? 'Saving…' : 'Save'}
                  </SaveRecipientsBtn>
                </RecipientsControls>
                <RecipientsHint>Separate addresses with commas. You always receive the report; these people are copied in.</RecipientsHint>
              </RecipientsRow>
            )}
          </SchedulePanel>
          <AuditTab
          portfolioId={id}
          auditSchedule={currentSchedule}
          ownerName={portfolio.owner?.name}
          ownerEmail={portfolio.owner?.email}
          isOwner={isOwner}
        />
        </>
      )}
      {activeTab === 'guru' && hasAnyBpAccess && (
        <ChatContainer>
          <GuruChat portfolioId={id} portfolioName={portfolio.name} buildingCount={bps.length} />
        </ChatContainer>
      )}
      {activeTab === 'tasks' && hasAnyBpAccess && anyBpHasTaskManagement && (
        <TabContent>
          <PortfolioTasksTab portfolioId={id} />
        </TabContent>
      )}
      {activeTab === 'actions' && hasAnyBpAccess && anyBpHasTaskManagement && <RemedialsWorkspace scope={{ kind: 'portfolio', id }} scopedBps={bps} />}
      {activeTab === 'incidents' && hasAnyBpAccess && anyBpHasTaskManagement && <IncidentsPage portfolioId={id} />}
      {activeTab === 'access' && canEdit && <AccessManager scopeType='portfolio' scopeId={id} scopeName={portfolio.name} canManage={canEdit} isOwner={isOwner} onUsersChanged={updateAccessCount} />}
      {activeTab === 'activity' && <ActivityLog targetType='portfolio' targetId={id} />}

      {editing && (
        <CreatePortfolioModal
          open
          onClose={() => setEditing(false)}
          onSubmit={handleEditPortfolio}
          initialValues={portfolio}
        />
      )}
      <PortfolioOwnerDialog
        open={editingOwner}
        value={portfolio.ownerOrganisation}
        onClose={() => setEditingOwner(false)}
        onSave={ownerOrganisation => {
          dispatch(updatePortfolioRoutine({ id, ownerOrganisation }))
          setEditingOwner(false)
        }}
      />
    </DetailShell>
  )
}

export default PortfolioDetail

const SharePanel = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  margin: 0 0 20px;
  padding: 14px 16px 14px 18px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'};
  border-radius: 6px;
`

const SharePanelAccent = styled.span`
  position: absolute;
  left: 0;
  top: 14px;
  bottom: 14px;
  width: 2px;
  background: ${({ theme }) => theme.colors.primary};
  opacity: 0.55;
  border-radius: 0 2px 2px 0;
`

const SharePanelBody = styled.div`
  flex: 1;
  min-width: 0;
`

const SharePanelTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  letter-spacing: 0.01em;
`

const SharePanelMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#6b7280'};

  svg { vertical-align: middle; }
`

const MetaDot = styled.span`
  color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
`

const MetaAccent = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 500;
`

const SharePanelNote = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'};
  font-size: 12.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#555'};
`

const TabContent = styled.div``















const ChatContainer = styled.div`
  height: calc(100vh - 260px);
  min-height: 500px;
  border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'};
  border-radius: 8px;
  overflow: hidden;

  @media (max-width: 768px) {
    height: calc(100vh - 220px);
    min-height: 400px;
  }
`

const SchedulePanel = styled.div`
  min-width: 0;
  margin: 0 0 20px;
  padding: 0 0 18px;
  border-bottom: 1px solid rgba(12, 18, 41, 0.08);
`

const ScheduleHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 12px;
`

const ScheduleTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`

const ScheduleSub = styled.div`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
`

const ScheduleOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

const ScheduleOption = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: ${({ $active }) => ($active ? '#fef2f2' : '#ffffff')};
  border: 1px solid ${({ $active }) => ($active ? '#D24724' : '#e5e7eb')};
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  text-align: left;
  color: ${({ $active }) => ($active ? '#991b1b' : '#111827')};
  font-family: inherit;

  svg { color: ${({ $active }) => ($active ? '#D24724' : '#6b7280')}; }

  &:hover:not(:disabled) {
    border-color: ${({ $active }) => ($active ? '#D24724' : '#d1d5db')};
    background: ${({ $active }) => ($active ? '#fef2f2' : '#f9fafb')};
  }

  &:disabled { opacity: 0.6; cursor: wait; }

  span {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }
  strong { font-size: 13px; font-weight: 600; }
  em { font-size: 11px; font-style: normal; color: ${({ $active }) => ($active ? '#991b1b' : '#6b7280')}; }
`

const RecipientsRow = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`
const RecipientsLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
`
const RecipientsControls = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`
const RecipientsInput = styled.input`
  flex: 1;
  min-width: 220px;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  font-family: inherit;
  color: #111827;
  &:focus { outline: none; border-color: #D24724; }
  &:disabled { opacity: 0.6; }
`
const SaveRecipientsBtn = styled.button`
  height: 38px;
  padding: 0 16px;
  border: none;
  border-radius: 8px;
  background: #D24724;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  &:hover:not(:disabled) { filter: brightness(0.96); }
  &:disabled { opacity: 0.6; cursor: wait; }
`
const RecipientsHint = styled.div`
  margin-top: 6px;
  font-size: 11px;
  color: #6b7280;
`
